import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  fetchRecentLiveLogs,
  isLiveLogFetchPath,
  streamLiveLogs,
  type LiveLogCategory,
  type LiveLogEntry
} from "../../api/adminLiveLogsApi";

const MAX_VISIBLE = 500;

const formatTime = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3
  });
};

const categoryLabel: Record<LiveLogCategory, string> = {
  activity: "Actions",
  fetch: "Fetch",
  mongo: "Mongo",
  acl: "ACL"
};

const categoryBadgeClass: Record<LiveLogCategory, string> = {
  activity: "bg-emerald-100 text-emerald-900 border-emerald-200",
  fetch: "bg-sky-100 text-sky-800 border-sky-200",
  mongo: "bg-violet-100 text-violet-800 border-violet-200",
  acl: "bg-amber-100 text-amber-900 border-amber-200"
};

const CATEGORY_ORDER: LiveLogCategory[] = ["activity", "fetch", "mongo", "acl"];

function tryParseJson(text: string): unknown | null {
  const trimmed = text.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    return null;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

function prettyJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

function jsonPreview(value: unknown, raw: string): string {
  if (Array.isArray(value)) {
    return `[${value.length} items]`;
  }
  if (value !== null && typeof value === "object") {
    const keys = Object.keys(value as object);
    if (keys.length === 0) {
      return "{}";
    }
    const first = keys.slice(0, 3).join(", ");
    return keys.length > 3 ? `{ ${first}, … }` : `{ ${first} }`;
  }
  const oneLine = raw.replace(/\s+/g, " ").trim();
  return oneLine.length > 72 ? `${oneLine.slice(0, 72)}…` : oneLine;
}

function CollapsibleJsonBlock({ label, raw }: { label: string; raw: string }) {
  const parsed = useMemo(() => tryParseJson(raw), [raw]);
  const display = parsed != null ? prettyJson(parsed) : raw;
  const preview = parsed != null ? jsonPreview(parsed, raw) : raw.replace(/\s+/g, " ").trim().slice(0, 72);

  return (
    <details className="group mt-1 rounded border border-gray-800 bg-gray-900/40">
      <summary className="cursor-pointer select-none list-none flex items-center gap-1.5 px-2 py-1.5 text-[11px] text-gray-400 hover:text-gray-200 [&::-webkit-details-marker]:hidden">
        <ChevronRight className="h-3.5 w-3.5 shrink-0 transition-transform group-open:rotate-90" />
        <span className="font-medium text-gray-300">{label}</span>
        <span className="truncate text-gray-500">{preview}</span>
      </summary>
      <pre className="max-h-64 overflow-auto border-t border-gray-800 px-2 py-2 text-[11px] leading-relaxed text-gray-300 whitespace-pre-wrap break-all m-0">
        {display}
      </pre>
    </details>
  );
}

function entrySummary(entry: LiveLogEntry): string {
  if (entry.category === "activity") {
    const who = entry.userEmail || entry.userId || "unknown user";
    const target = [entry.collection, entry.documentId].filter(Boolean).join(" / ");
    const detail = entry.detail ? ` (${entry.detail})` : "";
    return `${entry.action || "action"} ${who}${target ? ` — ${target}` : ""}${detail}`;
  }
  if (entry.category === "fetch") {
    return `${entry.method || "GET"} ${entry.path || "/"} → ${entry.status ?? "?"} (${entry.durationMs ?? 0}ms)`;
  }
  if (entry.category === "mongo") {
    const idPart = entry.documentId ? ` uid=${entry.documentId}` : "";
    return `${entry.operation || "write"} ${entry.collection || "?"}${idPart}`;
  }
  const diagnostics = entry.diagnostics as { bulk?: boolean; byAction?: Record<string, { allowed: number; denied: number }> } | null | undefined;
  if (diagnostics?.bulk) {
    const parts = Object.entries(diagnostics.byAction || {}).map(
      ([action, counts]) => `${action}: ${counts.allowed}a/${counts.denied}d`
    );
    return `BULK ${entry.reason || "check"}${parts.length ? ` (${parts.join(", ")})` : ""}`;
  }
  const verdict = entry.allowed ? "ALLOW" : "DENY";
  const target = [entry.targetType, entry.targetId].filter(Boolean).join(":");
  const base = `${verdict} ${entry.action || "?"} on ${target || "?"}`;
  if (entry.allowed) {
    return base;
  }
  const hint = entry.diagnostics?.hints?.[0];
  if (hint) {
    const short = hint.length > 80 ? `${hint.slice(0, 77)}…` : hint;
    return `${base} — ${short}`;
  }
  if (entry.diagnostics?.grantsForAction != null) {
    return `${base} — ${entry.diagnostics.grantsForAction} grant(s) for action`;
  }
  return base;
}

function LiveLogEntryDetails({ entry }: { entry: LiveLogEntry }) {
  if (entry.category === "activity") {
    const lines = [
      `User: ${entry.userEmail || entry.userId || "(unknown)"}`,
      entry.userId ? `Uid: ${entry.userId}` : null,
      entry.collection ? `Collection: ${entry.collection}` : null,
      entry.documentId ? `Item: ${entry.documentId}` : null,
      entry.detail ? `Detail: ${entry.detail}` : null,
      entry.path ? `${entry.method || "GET"} ${entry.path}` : null
    ].filter(Boolean);
    return (
      <pre className="text-[11px] text-gray-400 whitespace-pre-wrap break-all m-0">
        {lines.join("\n")}
      </pre>
    );
  }

  if (entry.category === "fetch") {
    return (
      <div className="space-y-1">
        <p className="text-[11px] text-gray-400 m-0">
          User: {entry.userId || "(anonymous)"}
          {entry.userEmail ? ` (${entry.userEmail})` : ""}
        </p>
        {entry.responseBody != null && entry.responseBody !== "" && (
          <CollapsibleJsonBlock label="Response" raw={entry.responseBody} />
        )}
      </div>
    );
  }

  if (entry.category === "mongo") {
    const summary = entry.summary?.trim();
    const summaryIsJson = summary ? tryParseJson(summary) != null : false;
    return (
      <div className="space-y-1">
        {summary && summaryIsJson && <CollapsibleJsonBlock label="Summary" raw={summary} />}
        {summary && !summaryIsJson && (
          <p className="text-[11px] text-gray-400 m-0">Summary: {summary}</p>
        )}
        {entry.ok === false && entry.errorMessage && (
          <p className="text-[11px] text-red-400 m-0">Error: {entry.errorMessage}</p>
        )}
        {!summary && entry.ok !== false && <p className="text-[11px] text-gray-500 m-0">—</p>}
      </div>
    );
  }

  const diagnostics = entry.diagnostics as {
    bulk?: boolean;
    total?: number;
    allowed?: number;
    denied?: number;
    byAction?: Record<string, { allowed: number; denied: number }>;
    sampleDenies?: Array<{ action?: string; targetType?: string; targetId?: string; reason?: string }>;
    hints?: string[];
    grantsForAction?: number;
    grantsTotal?: number;
    nearMisses?: unknown[];
    bypass?: Record<string, unknown>;
    collectionUid?: string | null;
  } | null | undefined;
  if (diagnostics?.bulk) {
    const lines = [
      `Reason: ${entry.reason || "—"}`,
      diagnostics.total != null ? `Checks: ${diagnostics.total}` : null,
      diagnostics.allowed != null ? `Allowed: ${diagnostics.allowed}` : null,
      diagnostics.denied != null ? `Denied: ${diagnostics.denied}` : null,
      `User: ${entry.userId || "(anonymous)"}`,
      entry.userGroups?.length ? `Groups: ${entry.userGroups.join(", ")}` : null,
      entry.userRoles?.length ? `Roles: ${entry.userRoles.join(", ")}` : null,
    ].filter(Boolean);
    return (
      <div className="space-y-1">
        <pre className="text-[11px] text-gray-400 whitespace-pre-wrap break-all m-0">
          {lines.join("\n")}
        </pre>
        {diagnostics.sampleDenies?.length ? (
          <CollapsibleJsonBlock
            label="Sample denies"
            raw={JSON.stringify(diagnostics.sampleDenies)}
          />
        ) : null}
        <CollapsibleJsonBlock label="By action" raw={JSON.stringify(diagnostics.byAction ?? {})} />
      </div>
    );
  }
  const lines = [
    `Reason: ${entry.reason || "—"}`,
    entry.layer ? `Layer: ${entry.layer}` : null,
    entry.fieldKey ? `Field: ${entry.fieldKey}` : null,
    entry.collectionUid ? `Collection: ${entry.collectionUid}` : null,
    entry.itemOwnerId ? `Item owner: ${entry.itemOwnerId}` : null,
    `User: ${entry.userId || "(anonymous)"}`,
    entry.userGroups?.length ? `Groups: ${entry.userGroups.join(", ")}` : null,
    entry.userRoles?.length ? `Roles: ${entry.userRoles.join(", ")}` : null,
    entry.systemAdmin ? "System admin: yes" : null,
    entry.matchedCount != null ? `Matched permissions: ${entry.matchedCount}` : null,
    entry.matchedUids?.length ? `Matched UIDs: ${entry.matchedUids.join(", ")}` : null,
    diagnostics?.grantsForAction != null
      ? `Grants for action: ${diagnostics.grantsForAction} (${diagnostics.grantsTotal ?? "?"} total)`
      : null
  ].filter(Boolean);

  return (
    <div className="space-y-1">
      <pre className="text-[11px] text-gray-400 whitespace-pre-wrap break-all m-0">
        {lines.join("\n")}
      </pre>
      {diagnostics?.hints?.length ? (
        <ul className="text-[11px] text-amber-200/90 list-disc pl-4 m-0 space-y-0.5">
          {diagnostics.hints.map((hint) => (
            <li key={hint}>{hint}</li>
          ))}
        </ul>
      ) : null}
      {diagnostics ? (
        <CollapsibleJsonBlock label="Diagnostics" raw={JSON.stringify(diagnostics)} />
      ) : null}
    </div>
  );
}

const AdminLiveLogsPanel = () => {
  const [entries, setEntries] = useState<LiveLogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [paused, setPaused] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [categories, setCategories] = useState<Record<LiveLogCategory, boolean>>({
    activity: true,
    fetch: true,
    mongo: true,
    acl: true
  });
  const [userFilter, setUserFilter] = useState("");
  const [showLiveLogFetches, setShowLiveLogFetches] = useState(false);
  const [aclAllowed, setAclAllowed] = useState(true);
  const [aclDenied, setAclDenied] = useState(true);
  const [expandedEntryIds, setExpandedEntryIds] = useState<Set<string>>(() => new Set());

  const seenIdsRef = useRef<Set<string>>(new Set());
  const listRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  const activeCategories = useMemo(
    () =>
      (Object.keys(categories) as LiveLogCategory[]).filter((key) => categories[key]),
    [categories]
  );

  const visibleEntries = useMemo(() => {
    let list = entries;
    if (!showLiveLogFetches) {
      list = list.filter(
        (entry) => !(entry.category === "fetch" && isLiveLogFetchPath(entry.path))
      );
    }
    const userQuery = userFilter.trim().toLowerCase();
    if (userQuery) {
      list = list.filter((entry) => {
        const haystack = `${entry.userEmail || ""} ${entry.userId || ""}`.toLowerCase();
        return haystack.includes(userQuery);
      });
    }
    if (categories.acl) {
      list = list.filter((entry) => {
        if (entry.category !== "acl") {
          return true;
        }
        if (entry.allowed && aclAllowed) {
          return true;
        }
        if (!entry.allowed && aclDenied) {
          return true;
        }
        return false;
      });
    }
    return list;
  }, [entries, showLiveLogFetches, categories.acl, aclAllowed, aclDenied, userFilter]);

  const appendEntry = useCallback(
    (entry: LiveLogEntry) => {
      if (pausedRef.current) {
        return;
      }
      if (!activeCategories.includes(entry.category)) {
        return;
      }
      if (seenIdsRef.current.has(entry.id)) {
        return;
      }
      seenIdsRef.current.add(entry.id);
      setEntries((prev) => {
        const next = [...prev, entry];
        if (next.length > MAX_VISIBLE) {
          return next.slice(next.length - MAX_VISIBLE);
        }
        return next;
      });
    },
    [activeCategories]
  );

  const loadInitial = useCallback(async () => {
    setError(null);
    const response = await fetchRecentLiveLogs({
      limit: 300,
      categories: activeCategories.length ? activeCategories : undefined
    });
    seenIdsRef.current = new Set();
    const list = response.entries || [];
    for (const entry of list) {
      seenIdsRef.current.add(entry.id);
    }
    setEntries(list.slice(-MAX_VISIBLE));
  }, [activeCategories]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const run = async () => {
      try {
        await loadInitial();
        if (cancelled) {
          return;
        }
        setConnected(true);
        await streamLiveLogs({
          categories: activeCategories.length ? activeCategories : undefined,
          signal: controller.signal,
          onEntry: appendEntry
        });
        if (!cancelled) {
          setConnected(false);
        }
      } catch (err: unknown) {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) {
          return;
        }
        const message = err instanceof Error ? err.message : "Live log stream failed";
        setError(message);
        setConnected(false);
      }
    };

    run();

    return () => {
      cancelled = true;
      controller.abort();
      setConnected(false);
    };
  }, [activeCategories, appendEntry, loadInitial]);

  useEffect(() => {
    if (!autoScroll || paused || !listRef.current) {
      return;
    }
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [visibleEntries, autoScroll, paused]);

  const toggleCategory = (key: LiveLogCategory) => {
    setCategories((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleClear = () => {
    seenIdsRef.current.clear();
    setEntries([]);
    setExpandedEntryIds(new Set());
  };

  const toggleEntryExpanded = (id: string) => {
    setExpandedEntryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="space-y-4 pt-2">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {CATEGORY_ORDER.map((key) => (
            <div key={key} className="flex items-center gap-2">
              <Checkbox
                id={`live-log-${key}`}
                checked={categories[key]}
                onCheckedChange={() => toggleCategory(key)}
              />
              <Label htmlFor={`live-log-${key}`} className="text-sm cursor-pointer">
                {categoryLabel[key]}
              </Label>
            </div>
          ))}
          {categories.acl && (
            <>
              <div className="flex items-center gap-2 border-l border-gray-300 pl-4 dark:border-gray-600">
                <Checkbox
                  id="live-log-acl-allowed"
                  checked={aclAllowed}
                  onCheckedChange={(checked) => setAclAllowed(checked === true)}
                />
                <Label htmlFor="live-log-acl-allowed" className="text-sm cursor-pointer">
                  Allowed
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="live-log-acl-denied"
                  checked={aclDenied}
                  onCheckedChange={(checked) => setAclDenied(checked === true)}
                />
                <Label htmlFor="live-log-acl-denied" className="text-sm cursor-pointer">
                  Denied
                </Label>
              </div>
            </>
          )}
          <div className="flex items-center gap-2 border-l border-gray-300 pl-4 dark:border-gray-600">
            <Label htmlFor="live-log-user" className="text-sm">
              User
            </Label>
            <input
              id="live-log-user"
              value={userFilter}
              onChange={(event) => setUserFilter(event.target.value)}
              placeholder="email or uid"
              className="h-8 w-44 rounded-md border border-input bg-background px-2 text-sm text-foreground"
            />
          </div>
          <div className="flex items-center gap-2 border-l border-gray-300 pl-4 dark:border-gray-600">
            <Checkbox
              id="live-log-show-self"
              checked={showLiveLogFetches}
              onCheckedChange={(checked) => setShowLiveLogFetches(checked === true)}
            />
            <Label htmlFor="live-log-show-self" className="text-sm cursor-pointer">
              Live log traffic
            </Label>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={connected ? "default" : "secondary"}>
            {connected ? "Live" : "Disconnected"}
          </Badge>
          <Button variant="outline" size="sm" onClick={() => setPaused((p) => !p)}>
            {paused ? "Resume" : "Pause"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAutoScroll((a) => !a)}>
            Auto-scroll {autoScroll ? "on" : "off"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => void loadInitial()}>
            Reload buffer
          </Button>
          <Button variant="outline" size="sm" onClick={handleClear}>
            Clear view
          </Button>
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      <div
        ref={listRef}
        className="rounded-md border bg-gray-950 text-gray-100 font-mono text-xs max-h-[60vh] overflow-y-auto"
      >
        {visibleEntries.length === 0 && (
          <p className="p-4 text-gray-400">
            Waiting for activity. Sign in, open a collection, or edit an item to record a user action.
          </p>
        )}
        {visibleEntries.map((entry) => {
          const expanded = expandedEntryIds.has(entry.id);
          return (
            <div key={entry.id} className="border-b border-gray-800 hover:bg-gray-900/80">
              <button
                type="button"
                onClick={() => toggleEntryExpanded(entry.id)}
                className="w-full text-left px-3 py-2 flex flex-wrap items-center gap-2"
              >
                <ChevronRight
                  className={`h-3.5 w-3.5 shrink-0 text-gray-500 transition-transform ${expanded ? "rotate-90" : ""}`}
                />
                <span className="text-gray-500 shrink-0">{formatTime(entry.at)}</span>
                <Badge className={`text-[10px] ${categoryBadgeClass[entry.category]}`}>
                  {categoryLabel[entry.category]}
                </Badge>
                {entry.category === "acl" && (
                  <Badge
                    className={
                      entry.allowed
                        ? "bg-green-900/50 text-green-300 border-green-800"
                        : "bg-red-900/50 text-red-300 border-red-800"
                    }
                  >
                    {entry.allowed ? "allowed" : "denied"}
                  </Badge>
                )}
                <span className="text-gray-100 break-all">{entrySummary(entry)}</span>
              </button>
              {expanded && (
                <div className="px-3 pb-2 pl-8">
                  <LiveLogEntryDetails entry={entry} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminLiveLogsPanel;
